import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  BookMarked, LogIn, Loader2, ArrowLeft, Mail, CreditCard,
  AlertCircle, Lock, User, Phone, UserPlus, KeyRound, ChevronRight, CheckCircle2, Calendar
} from 'lucide-react';
import axios from 'axios';
import { withEditorRef } from '../lib/editorRef';
import { ForgotPasswordLink } from './ForgotPassword';
import { TermsAcceptModal, TermsCheckbox } from './TermsAccept';
import { AutoRenewNote } from './ReaderBilling';
import { CheckoutPanel } from './CheckoutPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ACCENT = '#68AAB0';

const formatApiError = (detail) => {
  if (!detail) return 'Algo deu errado. Tente novamente.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  if (detail.code === 'catalog_suspended') return `${detail.message}${detail.email ? ` E-mail: ${detail.email}` : ''}`;
  return String(detail);
};

const formatCPF = (value) => {
  const d = value.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
};

const ageFrom = (iso) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso || '')) return null;
  const [y, m, d] = iso.split('-').map(Number);
  const t = new Date();
  const age = t.getFullYear() - y - ((t.getMonth() + 1 < m || (t.getMonth() + 1 === m && t.getDate() < d)) ? 1 : 0);
  return age >= 0 && age <= 120 ? age : null;
};

const Field = ({ id, label, icon: Icon, testId, ...props }) => (
  <div style={{ marginBottom: '1rem' }}>
    <Label htmlFor={id}>{label}</Label>
    <div style={{ position: 'relative' }}>
      <Icon className="w-4 h-4" style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#a0aec0' }} />
      <Input id={id} style={{ paddingLeft: '2.5rem' }} data-testid={testId} {...props} />
    </div>
  </div>
);

const ErrorBox = ({ message }) => message ? (
  <div data-testid="error-message" style={{ background: '#fed7d7', color: '#9b2c2c', padding: '0.75rem', borderRadius: '8px', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
    <AlertCircle className="w-4 h-4" style={{ flexShrink: 0 }} />
    {message}
  </div>
) : null;

const SubmitButton = ({ submitting, disabled = false, icon: Icon, label, busyLabel, testId }) => (
  <Button type="submit" style={{ width: '100%', background: ACCENT }} disabled={submitting || disabled} data-testid={testId}>
    {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Icon className="w-4 h-4 mr-2" />}
    {submitting ? busyLabel : label}
  </Button>
);

const formatBRL = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const statusLabel = { active: 'Ativa', pending: 'Pendente', expired: 'Expirada', suspended: 'Suspensa', platform_suspended: 'indisponível — plataforma suspensa' };

const PlatformList = ({ platforms, onSelect, selecting, readerToken }) => (
  <div data-testid="platform-list" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
    {platforms.map((p) => {
      const active = p.status === 'active';
      return (
        <React.Fragment key={p.editor_id}>
        <button
          key={p.editor_id}
          type="button"
          disabled={(!active && p.status !== 'pending') || selecting}
          onClick={() => onSelect(p.editor_id)}
          data-testid={`platform-option-${p.editor_id}`}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem', borderRadius: '10px', border: `1px solid ${active ? ACCENT : '#e2e8f0'}`, background: active ? '#f0fafa' : '#f7fafc', cursor: active ? 'pointer' : 'not-allowed', opacity: active ? 1 : 0.7, textAlign: 'left', transition: 'transform 0.15s ease, background-color 0.15s ease' }}
        >
          <div>
            <p style={{ fontWeight: 600, color: '#2D3748' }}>{p.nome}</p>
            <p style={{ fontSize: '0.8rem', color: active ? '#2f855a' : '#b7791f' }}>Assinatura {statusLabel[p.status] || p.status}</p>
          </div>
          {selecting === p.editor_id ? <Loader2 className="w-5 h-5 animate-spin" style={{ color: ACCENT }} /> : <ChevronRight className="w-5 h-5" style={{ color: active ? ACCENT : '#cbd5e0' }} />}
        </button>
        <AutoRenewNote platform={p} readerToken={readerToken} />
        </React.Fragment>
      );
    })}
  </div>
);

const SubscriberLogin = ({ editorId, editorSlug }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect');
  const initialStep = searchParams.get('step') === 'select' ? 'select' : searchParams.get('action') === 'subscribe' ? 'register' : 'login';

  const [loading, setLoading] = useState(Boolean(editorId));
  const [step, setStep] = useState(initialStep);
  const [submitting, setSubmitting] = useState(false);
  const [selecting, setSelecting] = useState(null);
  const [editor, setEditor] = useState(null);
  const [valorMensal, setValorMensal] = useState(0);
  const [cardCheckout, setCardCheckout] = useState(null);
  const [isPublic, setIsPublic] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [session, setSession] = useState(null);
  const [form, setForm] = useState({ nome: '', email: '', cpf: '', telefone: '', nascimento: '', senha: '', confirmar: '' });
  const [adultOnly, setAdultOnly] = useState(false);
  const age = ageFrom(form.nascimento);
  const underage = adultOnly && age !== null && age < 18;
  const [termsChecked, setTermsChecked] = useState(false);
  const [termsPending, setTermsPending] = useState(null);
  const [pwd, setPwd] = useState({ current: '', next: '', confirm: '' });

  const set = (key) => (e) => setForm({ ...form, [key]: key === 'cpf' ? formatCPF(e.target.value) : e.target.value });

  useEffect(() => {
    if (!editorId) return;
    axios.get(`${API}/digital/${editorId}/catalog`)
      .then((r) => { setEditor(r.data.editor); setValorMensal(r.data.valor_assinatura || 0); setIsPublic(Boolean(r.data.editor?.plataforma_publica)); setAdultOnly(Boolean(r.data.editor?.adult_only)); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [editorId]);

  useEffect(() => {
    if (initialStep !== 'select') return;
    const token = localStorage.getItem('readerToken');
    if (!token) { setStep('login'); return; }
    axios.get(`${API}/reader/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setSession({ ...r.data, reader_token: token }))
      .catch(() => { localStorage.removeItem('readerToken'); setStep('login'); });
  }, [initialStep]);

  const selectPlatform = async (targetId, data = session) => {
    setError('');
    setSelecting(targetId);
    const target = data.platforms.find((p) => p.editor_id === targetId);
    if (target && ['pending', 'expired'].includes(target.status)) {
      try {
        const c = await axios.post(`${API}/reader/checkout/${targetId}?method=card`, {}, { headers: { Authorization: `Bearer ${data.reader_token}` } });
        if (c.data.card_form) {
          setCardCheckout({ amount: c.data.valor_mensal, email: c.data.payer_email, token: data.reader_token, editorId: targetId, name: target.nome });
          setStep('card');
          return;
        }
      } catch (err) {
        setError(formatApiError(err.response?.data?.detail));
        return;
      } finally {
        setSelecting(null);
      }
    }
    try {
      const r = await axios.post(`${API}/reader/select/${targetId}`, {}, { headers: { Authorization: `Bearer ${data.reader_token}` } });
      localStorage.setItem('subscriberToken', r.data.token);
      localStorage.setItem('subscriberData', JSON.stringify(r.data.subscriber));
      localStorage.setItem('readerPlatformsCount', String(data.platforms.filter((p) => p.status === 'active').length));
      navigate(redirect && targetId === editorId ? redirect : `/${data.platforms.find((p) => p.editor_id === targetId)?.slug || targetId}`);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setSelecting(null);
    }
  };

  const acceptReaderTerms = async () => {
    await axios.post(`${API}/reader/accept-terms`, {}, { headers: { Authorization: `Bearer ${termsPending.reader_token}` } });
    const data = { ...termsPending, terms_accepted: true };
    setTermsPending(null);
    proceed(data);
  };

  const proceed = (data) => {
    if (!data.terms_accepted) { setTermsPending(data); return; }
    localStorage.setItem('readerToken', data.reader_token);
    setSession(data);
    if (data.must_change_password) { setPwd((p) => ({ ...p, current: form.senha })); setStep('change'); return; }
    if (data.universal && editorId) { selectPlatform(editorId, data); return; }
    const suspendedHere = data.platforms.find((p) => p.editor_id === editorId && p.status === 'suspended');
    if (suspendedHere) {
      localStorage.removeItem('readerToken');
      setSession(null);
      setError(`Seu usuário está suspenso nesta biblioteca. Entre em contato com a editora pelo e-mail ${suspendedHere.contact_email}.`);
      return;
    }
    const active = data.platforms.filter((p) => p.status === 'active');
    const here = active.find((p) => p.editor_id === editorId);
    // Pending here: go straight to payment
    if (data.platforms.some((p) => p.editor_id === editorId && ['pending', 'expired'].includes(p.status))) { selectPlatform(editorId, data); return; }
    if (active.length === 1) { selectPlatform(active[0].editor_id, data); return; }
    if (here && redirect) { selectPlatform(here.editor_id, data); return; }
    if (active.length === 0) setInfo('Nenhuma assinatura ativa ainda. Ela será liberada após a confirmação do pagamento pela editora.');
    setStep('select');
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/reader/login`, { email: form.email, senha: form.senha });
      proceed(r.data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (form.senha.length < 6) { setError('A senha deve ter pelo menos 6 caracteres'); return; }
    if (form.senha !== form.confirmar) { setError('As senhas não coincidem'); return; }
    if (!termsChecked) { setError('É preciso aceitar os Termos de uso para se cadastrar'); return; }
    if (age === null) { setError('Informe uma data de nascimento válida'); return; }
    if (underage) { setError('Este catálogo é exclusivo para maiores de 18 anos.'); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/reader/register`, {
        editor_id: editorId, nome: form.nome, email: form.email,
        cpf: form.cpf.replace(/\D/g, ''), telefone: form.telefone || null, senha: form.senha, meses: 1, terms_accepted: termsChecked,
        data_nascimento: form.nascimento
      });
      const here = r.data.platforms.find((p) => p.editor_id === editorId);
      if (here?.status === 'active') { proceed(r.data); return; }
      localStorage.setItem('readerToken', r.data.reader_token);
      setSession(r.data);
      try {
        const c = await axios.post(`${API}/reader/checkout/${editorId}?method=card`, {}, { headers: { Authorization: `Bearer ${r.data.reader_token}` } });
        if (c.data.card_form) { setCardCheckout({ amount: c.data.valor_mensal, email: c.data.payer_email, token: r.data.reader_token, editorId }); setStep('card'); return; }
      } catch (e) {
        // payment unavailable: keep subscription pending
      }
      setInfo(`Cadastro realizado! O pagamento online ainda não está disponível; sua assinatura em ${editor?.nome || 'esta plataforma'} será liberada após a confirmação do pagamento pela editora.`);
      setStep(r.data.platforms.some((p) => p.status === 'active') ? 'select' : 'done');
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = async (e) => {
    e.preventDefault();
    setError('');
    if (pwd.next.length < 6) { setError('A nova senha deve ter pelo menos 6 caracteres'); return; }
    if (pwd.next !== pwd.confirm) { setError('As senhas não coincidem'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/reader/change-password`, { current_password: pwd.current, new_password: pwd.next }, { headers: { Authorization: `Bearer ${session.reader_token}` } });
      proceed({ ...session, must_change_password: false });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const switchStep = (next) => { setError(''); setInfo(''); setStep(next); };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'linear-gradient(135deg, #1a1f2e 0%, #2d3748 100%)' }}>
        <Loader2 className="w-12 h-12 animate-spin" style={{ color: ACCENT }} />
      </div>
    );
  }

  const titles = {
    login: isPublic ? 'Área do Leitor' : 'Área do Assinante',
    register: 'Criar conta de leitor',
    change: 'Defina uma nova senha',
    card: 'Pagamento da assinatura',
    select: 'Escolha a plataforma',
    done: 'Cadastro realizado'
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #1a1f2e 0%, #2d3748 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
      <div style={{ width: '100%', maxWidth: step === 'card' ? '920px' : '440px', transition: 'max-width 0.2s ease' }}>
        {editorId && (
          <Button variant="ghost" onClick={() => navigate(`/${editorSlug}`)} style={{ color: '#a0aec0', marginBottom: '1rem' }} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar ao catálogo
          </Button>
        )}

        <Card style={{ background: 'white' }}>
          <CardHeader style={{ textAlign: 'center' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '0.5rem' }}>
              <BookMarked className="w-8 h-8" style={{ color: ACCENT }} />
            </div>
            <CardTitle style={{ color: '#2D3748' }} data-testid="subscriber-step-title">{titles[step]}</CardTitle>
            <CardDescription>
              {step === 'select' ? 'Sua conta Livrum vale para todas as editoras que você assina' : (editor?.nome || 'Livrum Digital')}
            </CardDescription>
          </CardHeader>

          <CardContent>
            <ErrorBox message={error} />
            {info && (
              <div data-testid="info-message" style={{ background: '#e6fffa', color: '#234e52', padding: '0.75rem', borderRadius: '8px', marginBottom: '1rem', display: 'flex', gap: '0.5rem', fontSize: '0.9rem' }}>
                <CheckCircle2 className="w-4 h-4" style={{ flexShrink: 0, marginTop: 2 }} />
                {info}
              </div>
            )}

            {step === 'login' && (
              <form onSubmit={handleLogin} data-testid="login-form">
                <Field id="email" label="E-mail" icon={Mail} type="email" placeholder="seu@email.com" value={form.email} onChange={set('email')} required testId="email-input" />
                <Field id="password" label="Senha" icon={Lock} type="password" placeholder="Sua senha" value={form.senha} onChange={set('senha')} required testId="password-input" />
                <SubmitButton submitting={submitting} icon={LogIn} label="Entrar" busyLabel="Entrando..." testId="login-submit-btn" />
                <div style={{ textAlign: 'center' }}><ForgotPasswordLink kind="reader" color={ACCENT} /></div>
              </form>
            )}

            {step === 'register' && (
              <form onSubmit={handleRegister} data-testid="register-form">
                <Field id="nome" label="Nome completo" icon={User} placeholder="Seu nome" value={form.nome} onChange={set('nome')} required testId="register-name-input" />
                <Field id="reg-email" label="E-mail" icon={Mail} type="email" placeholder="seu@email.com" value={form.email} onChange={set('email')} required testId="register-email-input" />
                <Field id="reg-cpf" label="CPF" icon={CreditCard} placeholder="000.000.000-00" value={form.cpf} onChange={set('cpf')} required testId="register-cpf-input" />
                <Field id="reg-nascimento" label="Data de nascimento" icon={Calendar} type="date" max={new Date().toISOString().slice(0, 10)} value={form.nascimento} onChange={set('nascimento')} required testId="register-birthdate-input" />
                {adultOnly && (
                  <p data-testid={underage ? 'register-underage-error' : 'register-adult-note'} style={{ fontSize: '0.78rem', marginTop: '-0.6rem', marginBottom: '1rem', color: underage ? '#c53030' : '#718096' }}>
                    {underage ? 'Este catálogo é exclusivo para maiores de 18 anos. Não é possível continuar o cadastro.' : 'Catálogo exclusivo para maiores de 18 anos.'}
                  </p>
                )}
                <Field id="telefone" label="Telefone (opcional)" icon={Phone} placeholder="(00) 00000-0000" value={form.telefone} onChange={set('telefone')} testId="register-phone-input" />
                <Field id="reg-senha" label="Crie sua senha" icon={Lock} type="password" placeholder="Mínimo 6 caracteres" value={form.senha} onChange={set('senha')} required testId="register-password-input" />
                <Field id="reg-confirmar" label="Confirme a senha" icon={Lock} type="password" placeholder="Repita a senha" value={form.confirmar} onChange={set('confirmar')} required testId="register-confirm-input" />
                {isPublic ? (
                  <p data-testid="register-public-note" style={{ fontSize: '0.85rem', color: '#2f855a', background: '#f0fff4', padding: '0.75rem', borderRadius: 8, marginBottom: '1rem' }}>
                    Biblioteca gratuita: depois do cadastro você já pode ler.
                  </p>
                ) : (
                <div style={{ marginBottom: '1rem' }}>
                  <div data-testid="register-total" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: '0.25rem', padding: '0.75rem 1rem', background: '#f0fafa', borderRadius: '8px', border: `1px solid ${ACCENT}` }}>
                    <span style={{ fontSize: '0.85rem', color: '#4a5568' }}>Assinatura mensal · cartão ou Pix</span>
                    <strong style={{ fontSize: '1.2rem', color: '#2D3748' }} data-testid="register-total-value">{formatBRL(valorMensal)}/mês</strong>
                  </div>
                </div>
                )}
                <p style={{ fontSize: '0.8rem', color: '#718096', marginBottom: '1rem' }}>
                  Já tem conta Livrum em outra editora? Use o mesmo e-mail, CPF e senha da sua conta para adicionar esta assinatura.
                </p>
                <TermsCheckbox checked={termsChecked} onChange={setTermsChecked} testId="register-terms-checkbox" />
                <SubmitButton submitting={submitting} disabled={underage} icon={UserPlus} label={isPublic ? 'Criar conta e começar a ler' : 'Criar conta e ir para o pagamento'} busyLabel="Cadastrando..." testId="register-submit-btn" />
              </form>
            )}

            {step === 'change' && (
              <form onSubmit={handleChange} data-testid="force-change-form">
                <p style={{ fontSize: '0.85rem', color: '#718096', marginBottom: '1rem' }}>Sua senha foi redefinida. Crie uma nova senha para continuar.</p>
                <Field id="cur" label="Senha atual" icon={Lock} type="password" value={pwd.current} onChange={(e) => setPwd({ ...pwd, current: e.target.value })} required testId="force-current-password-input" />
                <Field id="nxt" label="Nova senha" icon={KeyRound} type="password" placeholder="Mínimo 6 caracteres" value={pwd.next} onChange={(e) => setPwd({ ...pwd, next: e.target.value })} required testId="force-new-password-input" />
                <Field id="cnf" label="Confirme a nova senha" icon={KeyRound} type="password" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} required testId="force-confirm-password-input" />
                <SubmitButton submitting={submitting} icon={KeyRound} label="Salvar nova senha" busyLabel="Salvando..." testId="force-change-submit-btn" />
              </form>
            )}

            {step === 'card' && cardCheckout && (
              <CheckoutPanel editorId={cardCheckout.editorId} token={cardCheckout.token} amount={cardCheckout.amount} payerEmail={cardCheckout.email}
                editorName={cardCheckout.name || editor?.nome || 'esta plataforma'} color={ACCENT}
                onDone={() => window.location.assign(`${window.location.pathname}?step=select`)} />
            )}
            {step === 'select' && session && <PlatformList platforms={session.platforms} onSelect={selectPlatform} selecting={selecting} readerToken={session.reader_token} />}

            {(step === 'done' || (step === 'select' && session && !session.platforms.some((p) => p.status === 'active'))) && (
              <Button variant="outline" style={{ width: '100%', marginTop: '1rem' }} onClick={() => { localStorage.removeItem('readerToken'); setSession(null); switchStep('login'); }} data-testid="back-to-login-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar ao login
              </Button>
            )}

            {(step === 'login' || step === 'register') && editorId && (
              <div style={{ marginTop: '1.5rem', paddingTop: '1.5rem', borderTop: '1px solid #e2e8f0', textAlign: 'center' }}>
                <p style={{ color: '#718096', fontSize: '0.85rem' }}>{step === 'login' ? (isPublic ? 'Ainda não é leitor desta biblioteca?' : 'Ainda não é assinante?') : 'Já tem conta?'}</p>
                <button type="button" onClick={() => switchStep(step === 'login' ? 'register' : 'login')} data-testid="toggle-auth-mode-btn" style={{ color: ACCENT, fontWeight: 600, fontSize: '0.9rem', marginTop: '0.25rem', background: 'none', border: 'none', cursor: 'pointer' }}>
                  {step === 'login' ? 'Criar minha conta' : 'Fazer login'}
                </button>
              </div>
            )}
          </CardContent>
        </Card>

        <p data-testid="login-developed-by" style={{ textAlign: 'center', color: '#a0aec0', marginTop: '1.5rem', fontSize: '0.85rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
          Desenvolvido por
          <a href="/" style={{ display: 'inline-flex' }}>
            <img src="/livrum-logo-light.webp" alt="Livrum" style={{ height: '26px', width: 'auto', opacity: 0.7, filter: 'brightness(0) invert(1)' }} />
          </a>
        </p>
      </div>
      {termsPending && <TermsAcceptModal onAccept={acceptReaderTerms} onCancel={() => setTermsPending(null)} />}
    </div>
  );
};

export default withEditorRef(SubscriberLogin);
